import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
} from '@mui/material';
import { useEffect, useState } from 'react';

import { TEXAS_COUNTIES } from '../../lib/vcm/counties';
import type { TcrResource } from '../../types/vcm';

const STATUS_OPTIONS = ['Active', 'Inactive', 'Under Review'] as const;
const GEO_OPTIONS = ['Statewide', 'Regional', 'County-specific'] as const;

export interface ResourceFormValues {
  name: string;
  category: string;
  county: string;
  service: string;
  organization: string;
  status: string;
  geographicArea: string;
  contactInfo: string;
  eligibilityNotes: string;
}

function toValues(resource: TcrResource | null): ResourceFormValues {
  return {
    name: resource?.name ?? '',
    category: resource?.category ?? '',
    county: resource?.county ?? '',
    service: resource?.service ?? '',
    organization: resource?.organization ?? '',
    status: resource?.status ?? 'Active',
    geographicArea: resource?.geographicArea ?? 'Statewide',
    contactInfo: resource?.contactInfo ?? '',
    eligibilityNotes: resource?.eligibilityNotes ?? '',
  };
}

export interface ResourceFormDialogProps {
  readonly open: boolean;
  /** The resource being edited, or null when adding a new one. */
  readonly resource: TcrResource | null;
  readonly onClose: () => void;
  readonly onSave: (values: ResourceFormValues) => void;
}

/** Add/Edit resource modal with the full resource field set. Pure CDS/MUI. */
export function ResourceFormDialog({ open, resource, onClose, onSave }: ResourceFormDialogProps) {
  const [values, setValues] = useState<ResourceFormValues>(() => toValues(resource));

  useEffect(() => {
    if (open) setValues(toValues(resource));
  }, [open, resource]);

  const set = (key: keyof ResourceFormValues, value: string) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const canSave = values.name.trim().length > 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{resource ? 'Edit resource' : 'Add resource'}</DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mt: 0 }}>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Name"
              required
              fullWidth
              size="small"
              value={values.name}
              onChange={(e) => set('name', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Category"
              fullWidth
              size="small"
              value={values.category}
              onChange={(e) => set('category', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              select
              label="County"
              fullWidth
              size="small"
              value={values.county}
              onChange={(e) => set('county', e.target.value)}
            >
              <MenuItem value="">Select a county</MenuItem>
              {TEXAS_COUNTIES.map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Service"
              fullWidth
              size="small"
              value={values.service}
              onChange={(e) => set('service', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Organization"
              fullWidth
              size="small"
              value={values.organization}
              onChange={(e) => set('organization', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              select
              label="Status"
              fullWidth
              size="small"
              value={values.status}
              onChange={(e) => set('status', e.target.value)}
            >
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              select
              label="Geographic area"
              fullWidth
              size="small"
              value={values.geographicArea}
              onChange={(e) => set('geographicArea', e.target.value)}
            >
              {GEO_OPTIONS.map((g) => (
                <MenuItem key={g} value={g}>
                  {g}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Contact info"
              fullWidth
              size="small"
              value={values.contactInfo}
              onChange={(e) => set('contactInfo', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              label="Eligibility"
              fullWidth
              size="small"
              multiline
              minRows={2}
              value={values.eligibilityNotes}
              onChange={(e) => set('eligibilityNotes', e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!canSave} onClick={() => onSave(values)}>
          {resource ? 'Save changes' : 'Add resource'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
